// Engagement's parts (28 Oct 2026), shared by the waiting room, the leaderboard and the contribution page: the person's standing (points,
// level, streak, ranks), the 26-week activity grid, the badges (plain geometric seals, no mascots or faces), the three boards (people,
// departments, the waitlist) and the contribution panel (department, drop zone, what became of each file). Tokens only: every look, light
// and dark, English and Arabic.
import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { BookCheck, CheckCircle2, CircleDashed, CircleSlash, Copy, FileUp, Flame, Hourglass, Lock, LockKeyhole, PauseCircle, RotateCcw, ScanSearch, ShieldAlert, ShieldCheck, Upload, UserCheck, XCircle } from "lucide-react";
import { formatDate, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { toast } from "@ncp/ui";
import { Button, EconicCard, Notice, SegmentedControl, SkeletonBlock } from "../theme/primitives";
import { PopUp } from "../trust/parts";
import {
  ENGAGEMENT_KEY, deptName, personName, useChooseDepartment, useContributions, useEngagementDepartments, useLeaderboard, useRetryContribution, useSendFiles, useWaitlist,
  type Contribution, type ContributionState, type EngagementMe, type Period, type SendProgress,
} from "./api";
import { unitGroups } from "./units";

export const k = (key: string) => `assistant:econic.engagement.${key}`;

function useNum() {
  const { locale } = useLocale();
  return (n: number) => formatNumber(n, locale);
}

// ------------------------------------------------------------------ the person's standing

/** Points, level and its progress, the working-day streak and the ranks: the head of every engagement screen. */
export function StandingCard({ me, compact = false }: { me: EngagementMe; compact?: boolean }) {
  const { t } = useTranslation();
  const num = useNum();
  const { level } = me;
  const pct = level.next ? Math.min(100, Math.round(((me.points.total - level.floor) / (level.next - level.floor)) * 100)) : 100;
  return (
    <EconicCard className="eg-standing" title={t(k("standing.title"))} data-testid="eg-standing">
      <div className="eg-figures">
        <div className="fig"><b className="tnum">{num(me.points.total)}</b><span>{t(k("standing.points"))}</span></div>
        <div className="fig"><b className="tnum">{num(level.number)}</b><span>{t(k("standing.level"))}</span></div>
        <div className="fig streak" data-live={me.streak.activeToday || undefined}>
          <b className="tnum"><Flame aria-hidden />{num(me.streak.current)}</b>
          <span>{t(k("standing.streak"), { count: me.streak.current })}</span>
        </div>
      </div>
      <div className="eg-level" aria-label={t(k("standing.levelProgress"), { level: level.number })}>
        <div className="bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><span style={{ width: `${pct}%` }} /></div>
        <p className="hint">
          {level.next
            ? t(k("standing.toNext"), { points: num(level.next - me.points.total), level: level.number + 1 })
            : t(k("standing.top"))}
        </p>
      </div>
      {compact ? null : (
        <dl className="eg-ranks">
          <div><dt>{t(k("standing.thisWeek"))}</dt><dd className="tnum">{me.rank.week ? `#${num(me.rank.week)}` : "—"}<small>{t(k("standing.pts"), { n: num(me.points.week) })}</small></dd></div>
          <div><dt>{t(k("standing.thisMonth"))}</dt><dd className="tnum">{me.rank.month ? `#${num(me.rank.month)}` : "—"}<small>{t(k("standing.pts"), { n: num(me.points.month) })}</small></dd></div>
          <div><dt>{t(k("standing.inDepartment"))}</dt><dd className="tnum">{me.rank.department ? `#${num(me.rank.department)}` : "—"}<small>{me.rank.departmentOf ? t(k("standing.of"), { n: num(me.rank.departmentOf) }) : null}</small></dd></div>
          <div><dt>{t(k("standing.longest"))}</dt><dd className="tnum">{num(me.streak.longest)}<small>{t(k("standing.days"))}</small></dd></div>
        </dl>
      )}
      {!me.streak.activeToday && me.streak.current > 0 ? <p className="eg-nudge">{t(k("standing.keepStreak"))}</p> : null}
    </EconicCard>
  );
}

/** The last 26 weeks, a column a week (Monday at the top), each day shaded by the points it earned. */
export function ActivityGrid({ me }: { me: EngagementMe }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const num = useNum();
  const weeks = useMemo(() => {
    const byDay = new Map(me.days.map((d) => [d.day, d.points]));
    const today = new Date(`${me.today}T00:00:00Z`);
    const monday = new Date(today);
    monday.setUTCDate(today.getUTCDate() - ((today.getUTCDay() + 6) % 7) - 7 * 25);
    const cols: Array<Array<{ day: string; points: number; future: boolean }>> = [];
    for (let w = 0; w < 26; w += 1) {
      const col = [];
      for (let d = 0; d < 7; d += 1) {
        const at = new Date(monday);
        at.setUTCDate(monday.getUTCDate() + w * 7 + d);
        const day = at.toISOString().slice(0, 10);
        col.push({ day, points: byDay.get(day) ?? 0, future: at > today });
      }
      cols.push(col);
    }
    return cols;
  }, [me.days, me.today]);
  const level = (p: number) => (p <= 0 ? 0 : p < 10 ? 1 : p < 25 ? 2 : p < 50 ? 3 : 4);
  const activeDays = me.days.filter((d) => d.points > 0).length;
  return (
    <EconicCard className="eg-activity" title={t(k("activity.title"))} sub={t(k("activity.sub"), { count: activeDays, n: num(activeDays) })}>
      <div className="eg-grid" role="img" aria-label={t(k("activity.aria"), { count: activeDays, n: num(activeDays) })} dir="ltr">
        {weeks.map((col, i) => (
          <div key={i} className="wk">
            {col.map((c) => (
              <span key={c.day} className="d" data-l={c.future ? undefined : level(c.points)} data-future={c.future || undefined}
                title={c.future ? undefined : `${formatDate(c.day, locale)} · ${t(k("standing.pts"), { n: num(c.points) })}`} />
            ))}
          </div>
        ))}
      </div>
      <div className="eg-legend" aria-hidden><span>{t(k("activity.less"))}</span>{[0, 1, 2, 3, 4].map((l) => <span key={l} className="d" data-l={l} />)}<span>{t(k("activity.more"))}</span></div>
    </EconicCard>
  );
}

/** A badge's seal: the family's shape (diamond for files, circle for approvals, hexagon for streaks, square for questions) and its goal. */
function Seal({ family, goal, earned }: { family: string; goal: number; earned: boolean }) {
  const shape = family === "files" ? <path d="M16 2 30 16 16 30 2 16Z" />
    : family === "approved" ? <circle cx="16" cy="16" r="14" />
    : family === "streak" ? <path d="M16 2 28.1 9v14L16 30 3.9 23V9Z" />
    : <rect x="3" y="3" width="26" height="26" rx="4" />;
  return (
    <svg className="seal" viewBox="0 0 32 32" data-earned={earned || undefined} aria-hidden>
      <g className="shape">{shape}</g>
      <text x="16" y="16" dominantBaseline="central" textAnchor="middle">{goal}</text>
    </svg>
  );
}

const familyOf = (key: string) => key.split("-")[0]!.replace("first", "files");

export function Badges({ me }: { me: EngagementMe }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const earned = me.badges.filter((b) => b.earnedOn).length;
  return (
    <EconicCard className="eg-badges" title={t(k("badges.title"))} sub={t(k("badges.sub"), { earned, total: me.badges.length })}>
      <ul>
        {me.badges.map((b) => (
          <li key={b.key} data-earned={!!b.earnedOn || undefined}>
            <Seal family={familyOf(b.key)} goal={b.goal} earned={!!b.earnedOn} />
            <div className="tx">
              <b>{t(k(`badges.${b.key}.name`))}</b>
              <small>{b.earnedOn ? t(k("badges.earnedOn"), { date: formatDate(b.earnedOn, locale) }) : t(k(`badges.${b.key}.how`))}</small>
              {b.earnedOn ? null : <span className="bar" aria-hidden><span style={{ width: `${Math.round((b.progress / b.goal) * 100)}%` }} /></span>}
            </div>
          </li>
        ))}
      </ul>
    </EconicCard>
  );
}

export function HowPointsWork({ me }: { me: EngagementMe }) {
  const { t } = useTranslation();
  const r = me.rules;
  return (
    <EconicCard className="eg-rules" title={t(k("rules.title"))}>
      <ul>
        <li><span>{t(k("rules.fileSent"))}</span><b className="tnum">+{r.fileSent}</b></li>
        <li><span>{t(k("rules.fileApproved"))}</span><b className="tnum">+{r.fileApproved}</b></li>
        <li><span>{t(k("rules.activeDay"))}</span><b className="tnum">+{r.activeDay}</b></li>
        <li><span>{t(k("rules.question"), { n: r.questionsPerDay })}</span><b className="tnum">+{r.question}</b></li>
        <li><span>{t(k("rules.streak"), { days: r.milestones.map((m) => m.days).join(", ") })}</span><b className="tnum">+{r.milestones[0]?.bonus}–{r.milestones[r.milestones.length - 1]?.bonus}</b></li>
      </ul>
      <p className="hint">{t(k("rules.fine"))}</p>
    </EconicCard>
  );
}

// ------------------------------------------------------------------ the boards

const PERIODS: readonly Period[] = ["week", "month", "all"];

export function PeriodControl({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const { t } = useTranslation();
  return <SegmentedControl size="sm" label={t(k("period.label"))} value={value} onChange={onChange} options={PERIODS.map((p) => ({ value: p, label: t(k(`period.${p}`)) }))} />;
}

function Rank({ n }: { n: number }) {
  return <span className="eg-rank tnum" data-top={n <= 3 ? n : undefined}>{n}</span>;
}

function Monogram({ name }: { name: string }) {
  const letters = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return <span className="eg-mono" aria-hidden>{letters}</span>;
}

function BoardState({ loading, error, empty, onRetry, children }: { loading: boolean; error: unknown; empty: boolean; onRetry: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  if (loading) return <div className="flex flex-col gap-3 p-3" aria-busy>{[0, 1, 2, 3, 4].map((i) => <SkeletonBlock key={i} height={20} />)}</div>;
  if (error) return <div className="p-3"><Notice kind="error" action={<Button size="sm" onClick={onRetry}>{t(k("retry"))}</Button>}>{t(k("loadFailed"))}</Notice></div>;
  if (empty) return <div className="p-3"><Notice kind="empty">{t(k("board.empty"))}</Notice></div>;
  return <>{children}</>;
}

export function PeopleBoard({ period }: { period: Period }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const num = useNum();
  const q = useLeaderboard("people", period);
  const rows = q.data?.rows ?? [];
  const meOutside = q.data?.me && !rows.some((r) => r.me) ? q.data.me : null;
  return (
    <BoardState loading={q.isLoading} error={q.error} empty={!rows.length} onRetry={() => void q.refetch()}>
      <table className="ec-tbl eg-board" data-testid="eg-people-board">
        <thead>
          <tr>
            <th scope="col" className="lead rk">{t(k("board.rank"))}</th>
            <th scope="col">{t(k("board.person"))}</th>
            <th scope="col" className="hidden md:table-cell">{t(k("board.department"))}</th>
            <th scope="col" className="num hidden sm:table-cell">{t(k("board.streak"))}</th>
            <th scope="col" className="num hidden sm:table-cell">{t(k("board.files"))}</th>
            <th scope="col" className="num">{t(k("board.points"))}</th>
          </tr>
        </thead>
        <tbody>
          {[...rows, ...(meOutside ? [meOutside] : [])].map((r, i) => (
            <tr key={r.userId} aria-current={r.me || undefined} data-gap={meOutside && i === rows.length ? "" : undefined}>
              <td className="lead rk"><Rank n={r.rank} /></td>
              <td className="who">
                <Monogram name={r.name} />
                <span className="nm"><b>{personName(r, locale)}</b>{r.me ? <em>{t(k("board.you"))}</em> : null}{r.waitlist ? <small className="wl">{t(k("board.onWaitlist"))}</small> : null}
                  <small className="md:hidden">{deptName(r.department, locale)}</small></span>
                <span className="lv tnum" title={t(k("standing.levelN"), { n: r.level })}>{t(k("board.levelShort"), { n: num(r.level) })}</span>
              </td>
              <td className="hidden md:table-cell"><span className="clip">{deptName(r.department, locale) ?? "—"}</span></td>
              <td className="num hidden sm:table-cell tnum">{r.streak ? <span className="eg-streak"><Flame aria-hidden />{num(r.streak)}</span> : "—"}</td>
              <td className="num hidden sm:table-cell tnum">{num(r.files)}</td>
              <td className="num tnum"><b>{num(r.points)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </BoardState>
  );
}

export function DepartmentBoard({ period }: { period: Period }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const num = useNum();
  const q = useLeaderboard("departments", period);
  const rows = q.data?.rows ?? [];
  return (
    <BoardState loading={q.isLoading} error={q.error} empty={!rows.length} onRetry={() => void q.refetch()}>
      <table className="ec-tbl eg-board" data-testid="eg-department-board">
        <thead>
          <tr>
            <th scope="col" className="lead rk">{t(k("board.rank"))}</th>
            <th scope="col">{t(k("board.department"))}</th>
            <th scope="col" className="hidden md:table-cell">{t(k("board.leads"))}</th>
            <th scope="col" className="num hidden sm:table-cell">{t(k("board.people"))}</th>
            <th scope="col" className="num hidden sm:table-cell">{t(k("board.approved"))}</th>
            <th scope="col" className="num">{t(k("board.points"))}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.department.key} aria-current={r.mine || undefined}>
              <td className="lead rk"><Rank n={r.rank} /></td>
              <td className="who"><span className="nm"><b>{deptName(r.department, locale)}</b>{r.mine ? <em>{t(k("board.yours"))}</em> : null}
                {r.department.parent ? <small>{deptName(r.department.parent, locale)}</small> : r.department.kind === "sector" ? <small>{t(k("board.sectorItself"))}</small> : null}</span></td>
              <td className="hidden md:table-cell"><span className="clip">{personName(r.lead, locale)}</span></td>
              <td className="num hidden sm:table-cell tnum">{num(r.people)}</td>
              <td className="num hidden sm:table-cell tnum">{num(r.approved)}</td>
              <td className="num tnum"><b>{num(r.points)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </BoardState>
  );
}

export function WaitlistBoard({ limit }: { limit?: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const num = useNum();
  const q = useWaitlist();
  const all = q.data?.rows ?? [];
  const rows = limit ? all.slice(0, limit) : all;
  const meOutside = q.data?.me && !rows.some((r) => r.me) ? q.data.me : null;
  return (
    <BoardState loading={q.isLoading} error={q.error} empty={!all.length} onRetry={() => void q.refetch()}>
      <table className="ec-tbl eg-board" data-testid="eg-waitlist-board">
        <thead>
          <tr>
            <th scope="col" className="lead rk">{t(k("waitlist.place"))}</th>
            <th scope="col">{t(k("board.person"))}</th>
            <th scope="col" className="hidden md:table-cell">{t(k("board.department"))}</th>
            <th scope="col" className="num hidden sm:table-cell">{t(k("board.files"))}</th>
            <th scope="col" className="num">{t(k("board.points"))}</th>
          </tr>
        </thead>
        <tbody>
          {[...rows, ...(meOutside ? [meOutside] : [])].map((r, i) => (
            <tr key={r.userId} aria-current={r.me || undefined} data-gap={meOutside && i === rows.length ? "" : undefined}>
              <td className="lead rk"><Rank n={r.position} /></td>
              <td className="who"><Monogram name={r.name} /><span className="nm"><b>{personName(r, locale)}</b>{r.me ? <em>{t(k("board.you"))}</em> : null}
                <small className="md:hidden">{deptName(r.department, locale) ?? t(k("waitlist.noDepartment"))}</small></span></td>
              <td className="hidden md:table-cell"><span className="clip">{deptName(r.department, locale) ?? <span className="mut">{t(k("waitlist.noDepartment"))}</span>}</span></td>
              <td className="num hidden sm:table-cell tnum">{num(r.files)}</td>
              <td className="num tnum"><b>{num(r.points)}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
      {limit && all.length > limit ? <p className="eg-more">{t(k("waitlist.more"), { n: num(all.length - limit) })}</p> : null}
    </BoardState>
  );
}

// ------------------------------------------------------------------ contributions

/** The unit a person says they are from, chosen from the Ministry's units: each sector (the sector itself, then its departments), then the rest. */
export function DepartmentPicker({ me }: { me: EngagementMe }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const departments = useEngagementDepartments();
  const choose = useChooseDepartment();
  const groups = useMemo(() => unitGroups(departments.data ?? []), [departments.data]);
  const selectedReady = departments.data?.some((d) => d.key === me.department?.key && d.canContribute) ?? false;
  return (
    <div className="eg-dept">
      <label htmlFor="eg-dept-select">{t(k("contribute.department"))}</label>
      <PopUp id="eg-dept-select" value={selectedReady ? me.department?.key : ""} disabled={choose.isPending || departments.isLoading}
        onChange={(e) => {
          const key = e.target.value;
          if (departments.data?.some((d) => d.key === key && d.canContribute)) choose.mutate(key, { onError: (err) => toast.error(t(k("contribute.departmentFailed")), { description: err instanceof Error ? err.message : undefined }) });
        }}>
        <option value="" disabled>{t(k("contribute.choose"))}</option>
        {groups.map((g) => g.kind === "sector" ? (
          <optgroup key={g.key} label={deptName(g.sector, locale) ?? g.key}>
            {g.self ? <option value={g.self.key}>{t(k("welcome.sectorItself"))}</option> : null}
            {g.departments.map((c) => <option key={c.key} value={c.key}>{deptName(c, locale)}</option>)}
          </optgroup>
        ) : groups.length > 1 ? (
          <optgroup key={g.key} label={t(k("welcome.otherUnits"))}>
            {g.units.map((c) => <option key={c.key} value={c.key}>{deptName(c, locale)}</option>)}
          </optgroup>
        ) : g.units.map((c) => <option key={c.key} value={c.key}>{deptName(c, locale)}</option>))}
      </PopUp>
      {departments.isError ? <small role="alert">{t(k("loadFailed"))} <button type="button" onClick={() => void departments.refetch()}>{t(k("retry"))}</button></small>
        : me.department && !selectedReady && !departments.isLoading ? <small role="status">{t(k("contribute.chooseFirst"))}</small>
        : me.department && me.departmentClaimed ? <small>{t(k("contribute.claimed"))}</small> : null}
    </div>
  );
}

const STATE_ICON: Record<ContributionState, ReactNode> = {
  processing: <CircleDashed aria-hidden />, retry: <PauseCircle aria-hidden />, in_review: <Hourglass aria-hidden />, approved: <CheckCircle2 aria-hidden />, returned: <RotateCcw aria-hidden />,
  rejected: <XCircle aria-hidden />, duplicate: <Copy aria-hidden />, held: <ShieldAlert aria-hidden />, refused: <CircleSlash aria-hidden />, failed: <XCircle aria-hidden />,
};

function ContributionRow({ c }: { c: Contribution }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const num = useNum();
  const retry = useRetryContribution();
  return (
    <li className="eg-file" data-state={c.state}>
      <span className="ic">{STATE_ICON[c.state]}</span>
      <span className="tx">
        <b className="clip" dir="auto">{c.fileName ?? t(k("contribute.unnamed"))}</b>
        <small>{deptName(c.department, locale)} · {formatDate(c.at, locale)} · {t(k(`state.${c.state}`))}</small>
      </span>
      {c.state === "retry" ? (
        <Button size="sm" disabled={retry.isPending} onClick={() => retry.mutate(c.id, { onError: (e) => toast.error(t(k("contribute.retryFailed")), { description: e instanceof Error ? e.message : undefined }) })}>
          {t(k("retry"))}
        </Button>
      ) : null}
      <span className="pts tnum" data-zero={c.points === 0 || undefined}>{c.points ? `+${num(c.points)}` : "0"}</span>
    </li>
  );
}

/** Where the person sends their department's files: the department, the drop zone, the safety line and what became of each file. */
export function ContributePanel({ me }: { me: EngagementMe }) {
  const { t } = useTranslation();
  const num = useNum();
  const list = useContributions();
  const qc = useQueryClient();
  // A file that has passed its checks changes the person's points and place: the standing and the boards follow at once, together.
  const processing = list.data?.items.filter((i) => i.state === "processing").length ?? 0;
  const before = useRef(processing);
  useEffect(() => {
    if (processing < before.current) void qc.invalidateQueries({ queryKey: ENGAGEMENT_KEY });
    before.current = processing;
  }, [processing, qc]);
  const send = useSendFiles();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [progress, setProgress] = useState<SendProgress | null>(null);
  const busy = progress !== null;
  const ready = !!list.data?.portal && me.department?.canContribute === true;

  const run = async (files: File[]) => {
    if (!files.length || !ready || busy) return;
    setProgress({ name: files[0]!.name, done: 0, total: 1 });
    const results = await send(files, setProgress);
    setProgress(null);
    const ok = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok);
    if (ok) toast.success(t(k("contribute.sent"), { count: ok, n: num(ok) }), { description: t(k("contribute.sentHint")) });
    for (const f of failed) toast.error(t(k("contribute.failed"), { name: f.name }), { description: f.error });
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void run([...e.dataTransfer.files]);
  };

  return (
    <EconicCard className="eg-contribute" title={t(k("contribute.title"))} sub={t(k("contribute.sub"))} data-testid="eg-contribute">
      <DepartmentPicker me={me} />
      <div
        className="eg-drop" data-over={over || undefined} data-disabled={!ready || undefined} aria-busy={busy || undefined}
        onDragOver={(e) => { e.preventDefault(); if (ready) setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
      >
        <FileUp aria-hidden />
        {busy ? (
          <p className="st" role="status">{t(k("contribute.sending"), { name: progress.name, pct: Math.round((progress.done / progress.total) * 100) })}</p>
        ) : ready ? (
          <p className="st">{t(k("contribute.drop"))}</p>
        ) : (
          <p className="st">{t(k("contribute.chooseFirst"))}</p>
        )}
        <Button size="sm" variant="primary" icon={<Upload aria-hidden />} disabled={!ready || busy} onClick={() => input.current?.click()} data-testid="eg-choose-files">
          {t(k("contribute.chooseFiles"))}
        </Button>
        <input ref={input} type="file" multiple hidden onChange={(e) => { void run([...(e.target.files ?? [])]); e.target.value = ""; }} />
      </div>
      <SecurePath />
      <h3 className="eg-h3">{t(k("contribute.yours"))}</h3>
      {list.isLoading ? <SkeletonBlock height={40} /> : list.data?.items.length ? (
        <ul className="eg-files">{list.data.items.map((c) => <ContributionRow key={c.id} c={c} />)}</ul>
      ) : (
        <p className="eg-empty">{t(k("contribute.none"))}</p>
      )}
    </EconicCard>
  );
}

/** What happens to a file, in order (28 Oct 2026): encrypted on arrival, scanned for malware, checked for personal data, reviewed by the
 *  department, then in the Knowledge Bank. The same pipeline as KbUploadService; only the person who sent a file sees it. */
function SecurePath() {
  const { t } = useTranslation();
  const steps = [
    { key: "s1", icon: <LockKeyhole /> }, { key: "s2", icon: <ShieldCheck /> }, { key: "s3", icon: <ScanSearch /> },
    { key: "s4", icon: <UserCheck /> }, { key: "s5", icon: <BookCheck /> },
  ];
  return (
    <div className="eg-secure-path" data-testid="eg-secure-path">
      <p className="hd"><Lock aria-hidden />{t(k("contribute.secure.title"))}</p>
      <ol>
        {steps.map((s, i) => (
          <li key={s.key}><span className="ic" aria-hidden>{s.icon}</span><span className="tx"><small className="n tnum">{i + 1}</small>{t(k(`contribute.secure.${s.key}`))}</span></li>
        ))}
      </ol>
      <p className="only">{t(k("contribute.secure.only"))}</p>
    </div>
  );
}

/** A line under a heading linking to the full leaderboard. */
export function BoardLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={to} className="eg-link">{children}</Link>;
}
